import {
  getPaddleBillingFunction,
  getWorkspaceSubscriptionStatusFunction,
} from '@easy-expense/auth-client/src/Callable.backend';
import {
  useCachedMembers,
  useCurrentWorkspace,
  useIsAdminRole,
} from '@easy-expense/data-firestore-client';
import { PaddleBillingUsage, WorkspaceSubscriptionStatus } from '@easy-expense/data-schema-v2';
import { getTranslation } from '@easy-expense/intl-client';
import { Layout, OpenSans } from '@easy-expense/ui-web-core';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { onBillingChanged } from '../lib/Billing/billingChanged';
import { computeIsOverSeats } from '../lib/Billing/seatsOverage';
import { isFeatureEnabled } from '../lib/featureFlags';

export const SeatsBanner: React.FC = () => {
  const isAdmin = useIsAdminRole();
  const members = useCachedMembers();
  const currentWorkspace = useCurrentWorkspace();
  const navigate = useNavigate();
  const [billingUsage, setBillingUsage] = React.useState<PaddleBillingUsage | null>(null);
  const [workspaceSubscriptionStatus, setWorkspaceSubscriptionStatus] =
    React.useState<WorkspaceSubscriptionStatus | null>(null);
  // Bumped whenever billing settings reports a subscription change, so a purchase that covers the
  // overage clears the banner without a page reload.
  const [refreshCount, setRefreshCount] = React.useState(0);

  React.useEffect(() => onBillingChanged(() => setRefreshCount((count) => count + 1)), []);

  React.useEffect(() => {
    const workspaceID = currentWorkspace?.key;
    if (!isFeatureEnabled('seatsBanner') || !workspaceID) {
      return;
    }
    let cancelled = false;
    // Resolve coverage across providers first — a RevenueCat (App Store/Play Store) subscription
    // has no Paddle billing usage to read at all, so purchasedSeats would otherwise fall to 0 and
    // read as "no subscription" instead of that plan's real seat cap. Only fetch Paddle usage once
    // we know the workspace's coverage is actually Paddle's (or there's no coverage at all).
    getWorkspaceSubscriptionStatusFunction()({ workspaceID })
      .then((statusResp) => {
        if (cancelled) {
          return undefined;
        }
        setWorkspaceSubscriptionStatus(statusResp.data);
        if (statusResp.data.active && statusResp.data.subscriptionSource !== 'paddle') {
          return undefined;
        }
        return getPaddleBillingFunction()({ workspaceID }).then((usageResp) => {
          if (!cancelled) {
            setBillingUsage(usageResp.data);
          }
        });
      })
      .catch(() => {
        // Fail closed: leave billingUsage/workspaceSubscriptionStatus null so the banner stays hidden.
      });
    return () => {
      cancelled = true;
    };
  }, [currentWorkspace?.key, refreshCount]);

  // A RevenueCat-covered workspace has no Paddle billing usage at all (see effect above), so its
  // seat cap/usage come from workspaceSubscriptionStatus instead, which resolves seats correctly
  // for either provider.
  const isNonPaddleCoverage =
    workspaceSubscriptionStatus?.active === true &&
    workspaceSubscriptionStatus.subscriptionSource !== null &&
    workspaceSubscriptionStatus.subscriptionSource !== 'paddle';

  // purchasedSeats is already the real seat cap for whatever Paddle plan is active — 0 for no
  // subscription, the bundled entitlement for an individual plan (Starter/Professional), or the
  // purchased quantity for Team — so every Paddle plan participates in the same comparison below.
  const allowedSeats = isNonPaddleCoverage
    ? (workspaceSubscriptionStatus?.seatsAllowed ?? 0)
    : (billingUsage?.purchasedSeats ?? 0);
  const usedSeats = isNonPaddleCoverage
    ? (workspaceSubscriptionStatus?.seatsUsed ?? 0)
    : Math.max(billingUsage?.activeSeats ?? 0, members?.length ?? 0);
  const hasLoaded = isNonPaddleCoverage
    ? workspaceSubscriptionStatus !== null
    : billingUsage !== null;
  const isOverSeats = computeIsOverSeats({
    hasLoaded,
    usedSeats,
    allowedSeats,
  });

  if (!isFeatureEnabled('seatsBanner') || !isOverSeats) {
    return null;
  }

  return (
    <Layout.PressableRow
      bg="destructiveDark"
      center
      px
      py={8}
      onClick={() => {
        if (isAdmin) {
          navigate('/settings?tab=billing');
        } else {
          window.alert(
            getTranslation('Contact your workspace admin to add more seats to your subscription.'),
          );
        }
      }}
    >
      <OpenSans.Primary color="inverse" weight="bold-700">
        {`${usedSeats} ${getTranslation('of')} ${allowedSeats} ${getTranslation('seats used')} — ${getTranslation('click to manage billing')}`}
      </OpenSans.Primary>
    </Layout.PressableRow>
  );
};
